import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { SITE_COPY_CONTENT_KEY } from "@tih/shared";
import { EventStreamFields } from "../EventStreamFields";
import {
  EMPTY_STREAM_FORM,
  formToStream,
  streamToForm,
  type StreamForm,
} from "../../../lib/event-stream-form";
import {
  ApiError,
  adminFetchContent,
  adminFetchLive,
  adminPatchContent,
  adminSaveEventStream,
} from "../../../api/client";
import { useEventHub } from "../../../context/EventHubContext";
import { useSiteCopy } from "../../../context/SiteCopyContext";
import {
  extractYoutubeId,
  parseHomepageVideo,
  type HomepageVideo,
} from "../../../lib/homepage-video";

const EMPTY_HOME: HomepageVideo = {
  published: false,
  youtubeId: "",
  title: "",
  subtitle: "",
  eventId: null,
};

export function EventStreamPanel() {
  const hub = useEventHub();
  const { refresh: refreshCopy } = useSiteCopy();
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [landingBusy, setLandingBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [viewers, setViewers] = useState(0);
  const [draft, setDraft] = useState<StreamForm>(EMPTY_STREAM_FORM);
  const [savedJson, setSavedJson] = useState<ReturnType<typeof formToStream>>(null);
  const [homepageVideo, setHomepageVideo] = useState<HomepageVideo>(EMPTY_HOME);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      adminFetchLive(),
      adminFetchContent(SITE_COPY_CONTENT_KEY).catch((err) => {
        if (err instanceof ApiError && err.status === 404) {
          return { valueJson: {} };
        }
        throw err;
      }),
    ])
      .then(([res, content]) => {
        if (cancelled) return;
        const row = res.events.find((e) => e.id === hub.eventId);
        const form = streamToForm(row?.streamJson ?? hub.event?.streamJson ?? null);
        setDraft(form);
        setSavedJson(formToStream(form));
        setViewers(res.viewers);
        const parsed = parseHomepageVideo(
          (content.valueJson as Record<string, unknown> | undefined)?.homepageVideo,
        );
        setHomepageVideo(parsed ?? EMPTY_HOME);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof ApiError ? e.message : "Could not load stream");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [hub.eventId, hub.event?.streamJson]);

  const phase = hub.phase;
  const dirty = JSON.stringify(formToStream(draft)) !== JSON.stringify(savedJson);
  const isPast = phase === "past";
  const isOngoing = phase === "ongoing";
  const isFeatured = hub.featuredId === hub.eventId;
  const missingStream = !savedJson;
  const showEmptyStreamWarning = isFeatured && missingStream;

  const savedYoutubeId =
    savedJson?.youtubeId ||
    (savedJson?.source?.kind === "youtube" ? savedJson.source.src : null) ||
    null;
  const resolvedSavedYoutube = savedYoutubeId
    ? extractYoutubeId(savedYoutubeId) ?? savedYoutubeId.trim()
    : "";

  const landingShowsThis =
    Boolean(homepageVideo.published) &&
    Boolean(resolvedSavedYoutube) &&
    (homepageVideo.eventId === hub.eventId ||
      homepageVideo.youtubeId === resolvedSavedYoutube);

  const landingShowsOther =
    Boolean(homepageVideo.published) &&
    Boolean(homepageVideo.youtubeId) &&
    !landingShowsThis;

  async function saveStream() {
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      const saved = await adminSaveEventStream(hub.eventId, formToStream(draft));
      const row = saved.events.find((e) => e.id === hub.eventId);
      const form = streamToForm(row?.streamJson ?? null);
      setDraft(form);
      setSavedJson(formToStream(form));
      setViewers(saved.broadcastTo);
      await hub.refreshSummary();
      setSuccess(
        phase === "ongoing"
          ? `Saved and pushed to ${saved.broadcastTo} connected viewer${saved.broadcastTo === 1 ? "" : "s"}.`
          : "Stream link saved.",
      );
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not save stream");
    } finally {
      setBusy(false);
    }
  }

  async function clearStream() {
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      await adminSaveEventStream(hub.eventId, null);
      setDraft(EMPTY_STREAM_FORM);
      setSavedJson(null);
      await hub.refreshSummary();
      setSuccess("Stream link removed.");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not clear stream");
    } finally {
      setBusy(false);
    }
  }

  async function publishOnLanding() {
    if (!resolvedSavedYoutube) {
      setError("Save a YouTube stream link first, then publish it on the landing page.");
      return;
    }
    if (dirty) {
      setError("Save the stream link before publishing it on the landing page.");
      return;
    }
    setLandingBusy(true);
    setError(null);
    setSuccess(null);
    try {
      const payload: HomepageVideo = {
        published: true,
        youtubeId: resolvedSavedYoutube,
        title:
          homepageVideo.title.trim() ||
          hub.event?.title?.trim() ||
          "Together In Heaven · Worship Overnight",
        subtitle:
          homepageVideo.subtitle.trim() ||
          savedJson?.subtitle?.trim() ||
          "Full overnight replay",
        eventId: hub.eventId,
      };
      await adminPatchContent(SITE_COPY_CONTENT_KEY, { homepageVideo: payload });
      setHomepageVideo(payload);
      await refreshCopy();
      setSuccess(
        isOngoing
          ? "Landing page replay set. Visitors will see it on the home / coming-soon page when this broadcast ends."
          : "This replay is now on the public landing page (home / coming-soon).",
      );
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not publish on landing page");
    } finally {
      setLandingBusy(false);
    }
  }

  async function hideFromLanding() {
    setLandingBusy(true);
    setError(null);
    setSuccess(null);
    try {
      const payload: HomepageVideo = { ...homepageVideo, published: false };
      await adminPatchContent(SITE_COPY_CONTENT_KEY, { homepageVideo: payload });
      setHomepageVideo(payload);
      await refreshCopy();
      setSuccess("Landing page replay hidden. A live broadcast will still appear when one is on.");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not hide landing replay");
    } finally {
      setLandingBusy(false);
    }
  }

  if (loading) return <div style={{ opacity: 0.5 }}>Loading stream settings…</div>;

  const showLandingControls = isPast || isOngoing || Boolean(resolvedSavedYoutube);

  return (
    <>
      <section className="admin-card">
        <h2 className="admin-card-title">
          {isPast ? "Replay link" : "Live stream"}
        </h2>
        <p className="admin-card-foot" style={{ marginTop: 0, marginBottom: 16 }}>
          {phase === "ongoing"
            ? `Visitors see this stream on /live. ${viewers} viewer${viewers === 1 ? "" : "s"} connected now.`
            : isPast
              ? "Past events with a stream link appear in the replay archive on /live. Use Landing page below to put this night on the public home."
              : "When this event is ongoing, the stream goes live automatically on /live."}
        </p>
        {error && <div className="form-error">{error}</div>}
        {success && (
          <div className="form-success" style={{ padding: 12, marginBottom: 16 }}>
            {success}
          </div>
        )}
        {showEmptyStreamWarning && (
          <div className="form-error" role="status" style={{ marginBottom: 16 }}>
            This event is <strong>on the public home</strong>, but no stream source is set.
            Visitors on /live and the home video area have nothing to play until you save a
            YouTube or HLS link below.
          </div>
        )}
        <EventStreamFields
          form={draft}
          onChange={(next) => setDraft((prev) => ({ ...prev, ...next }))}
          disabled={busy || landingBusy}
          hint={
            phase === "ongoing" && draft.src.trim()
              ? "This stream is live while the event is ongoing."
              : isPast
                ? "Set a YouTube or HLS replay link for this worship night."
                : "Title comes from the event caption. Poster uses the event poster when set."
          }
        />
        <div className="admin-blog-form-actions">
          <button
            type="button"
            className="btn btn-primary"
            disabled={busy || landingBusy || !dirty}
            onClick={() => void saveStream()}
          >
            {busy ? "Saving…" : phase === "ongoing" ? "Save & push" : "Save link"}
          </button>
          {savedJson && (
            <button
              type="button"
              className="btn btn-ghost"
              disabled={busy || landingBusy}
              onClick={() => void clearStream()}
            >
              Remove stream
            </button>
          )}
        </div>
      </section>

      {showLandingControls && (
        <section className="admin-card" style={{ marginTop: 20 }}>
          <h2 className="admin-card-title">Landing page video</h2>
          <p className="admin-card-foot" style={{ marginTop: 0, marginBottom: 16 }}>
            Same control as Dashboard → Landing page video. Hide here when a new upcoming poster
            should lead the home page (the replay then sits underneath, or stays off if hidden).
            A live broadcast still takes over while this event is ongoing.
          </p>

          {landingShowsThis ? (
            <p className="admin-card-sub">
              Status: <strong>published on landing</strong>
              {resolvedSavedYoutube ? ` · YouTube ${resolvedSavedYoutube}` : null}
            </p>
          ) : landingShowsOther ? (
            <p className="admin-card-sub">
              Status: another replay is published on the landing page
              {homepageVideo.youtubeId ? ` (${homepageVideo.youtubeId})` : ""}. Publishing this one
              will replace it.
            </p>
          ) : (
            <p className="admin-card-sub">
              Status: <strong>not on the landing page</strong>
              {homepageVideo.youtubeId && !homepageVideo.published
                ? " (a draft id is saved but unpublished)"
                : ""}
            </p>
          )}

          {!resolvedSavedYoutube && (
            <p className="admin-card-sub">
              Landing publish needs a <strong>YouTube</strong> id. Save a YouTube link above first
              (HLS/MP4 still works for /live, but not for the homepage player yet).
            </p>
          )}

          <div className="admin-blog-form-actions">
            <button
              type="button"
              className="btn btn-primary"
              disabled={
                landingBusy ||
                busy ||
                dirty ||
                !resolvedSavedYoutube ||
                landingShowsThis
              }
              onClick={() => void publishOnLanding()}
            >
              {landingBusy
                ? "Saving…"
                : landingShowsThis
                  ? "On landing page"
                  : isOngoing
                    ? "Use on landing when live ends"
                    : "Show on landing page"}
            </button>
            {(landingShowsThis || homepageVideo.published) && (
              <button
                type="button"
                className="btn btn-ghost"
                disabled={landingBusy || busy}
                onClick={() => void hideFromLanding()}
              >
                Hide from landing page
              </button>
            )}
            <Link to="/admin" className="btn btn-ghost">
              Advanced homepage video
            </Link>
          </div>
        </section>
      )}
    </>
  );
}
